import React, { useEffect, useState } from "react";
import {
  Bookmark,
  Share2,
  Copy,
  Mail,
  Facebook,
  Twitter,
  MessageCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import placeholder from "../../assets/Car Image.png"; // Update with actual placeholder image
import api from "../../lib/api.ts";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

const CarDetailHeader = ({ car }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [vehicleList, setVehicleList] = useState([]);
  const [nextCar, setNextCar] = useState(null);
  const [previousCar, setPreviousCar] = useState(null);
  const [displayedCar, setDisplayedCar] = useState(null);
  const [currentNavigationIndex, setCurrentNavigationIndex] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [totalVehiclesInLane, setTotalVehiclesInLane] = useState(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showShareDropdown &&
        !event.target.closest(".share-dropdown-container")
      ) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showShareDropdown]);

  // Fetch wishlist on component mount to check if current vehicle is bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id || !car?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;

        if (wishlistData && Array.isArray(wishlistData)) {
          const isInWishlist = wishlistData.some(
            (item) => item.vehicleId === car.id
          );
          setIsBookmarked(isInWishlist);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id, car?.id]);

  // Function to get all vehicles organized by lane and run number
  function getAllVehiclesByLaneAndRun(vehicleList) {
    if (!Array.isArray(vehicleList) || vehicleList.length === 0) {
      return [];
    }

    // Group vehicles by lane
    const vehiclesByLane = {};
    vehicleList.forEach((vehicle) => {
      const laneId =
        vehicle.auctionLane?.laneId || vehicle.auctionLane?.lane?.id;
      const runNo = vehicle.runNo;

      if (laneId && runNo !== undefined) {
        if (!vehiclesByLane[laneId]) {
          vehiclesByLane[laneId] = [];
        }
        vehiclesByLane[laneId].push({
          ...vehicle,
          laneId,
          runNo,
        });
      }
    });

    // Sort vehicles within each lane by run number
    Object.keys(vehiclesByLane).forEach((laneId) => {
      vehiclesByLane[laneId].sort((a, b) => a.runNo - b.runNo);
    });

    return vehiclesByLane;
  }

  // Function to get all vehicles in sequential order
  function getAllVehiclesInOrder(vehicleList) {
    const vehiclesByLane = getAllVehiclesByLaneAndRun(vehicleList);
    const orderedVehicles = [];

    // Sort lanes and get vehicles in order
    const sortedLaneIds = Object.keys(vehiclesByLane).sort();
    sortedLaneIds.forEach((laneId) => {
      orderedVehicles.push(...vehiclesByLane[laneId]);
    });

    return orderedVehicles;
  }

  // Function to get next car in sequence
  function getNextCar(vehicleList, currentCarId) {
    const vehiclesByLane = getAllVehiclesByLaneAndRun(vehicleList);

    if (Object.keys(vehiclesByLane).length === 0) {
      return null;
    }

    // Find current car
    let currentCar = null;
    let currentLaneId = null;

    for (const laneId in vehiclesByLane) {
      const car = vehiclesByLane[laneId].find(
        (v) => v.vehicleId === currentCarId
      );
      if (car) {
        currentCar = car;
        currentLaneId = laneId;
        break;
      }
    }

    if (!currentCar) {
      return null;
    }

    const currentRunNo = currentCar.runNo;
    const currentLaneVehicles = vehiclesByLane[currentLaneId];

    // Check if there's a next car in the same lane
    const nextCarInSameLane = currentLaneVehicles.find(
      (v) => v.runNo === currentRunNo + 1
    );
    if (nextCarInSameLane) {
      return nextCarInSameLane;
    }

    // If no next car in same lane, find next lane and get first car
    const laneIds = Object.keys(vehiclesByLane).sort();
    const currentLaneIndex = laneIds.indexOf(currentLaneId);

    for (let i = currentLaneIndex + 1; i < laneIds.length; i++) {
      const nextLaneId = laneIds[i];
      const nextLaneVehicles = vehiclesByLane[nextLaneId];
      if (nextLaneVehicles && nextLaneVehicles.length > 0) {
        return nextLaneVehicles[0]; // First car in next lane
      }
    }

    return null; // No more cars
  }

  // Function to get previous car in sequence
  function getPreviousCar(vehicleList, currentCarId) {
    const vehiclesByLane = getAllVehiclesByLaneAndRun(vehicleList);

    if (Object.keys(vehiclesByLane).length === 0) {
      return null;
    }

    // Find current car
    let currentCar = null;
    let currentLaneId = null;

    for (const laneId in vehiclesByLane) {
      const car = vehiclesByLane[laneId].find(
        (v) => v.vehicleId === currentCarId
      );
      if (car) {
        currentCar = car;
        currentLaneId = laneId;
        break;
      }
    }

    if (!currentCar) {
      return null;
    }

    const currentRunNo = currentCar.runNo;
    const currentLaneVehicles = vehiclesByLane[currentLaneId];

    // Check if there's a previous car in the same lane
    const prevCarInSameLane = currentLaneVehicles.find(
      (v) => v.runNo === currentRunNo - 1
    );
    if (prevCarInSameLane) {
      return prevCarInSameLane;
    }

    // If no previous car in same lane, find previous lane and get last car
    const laneIds = Object.keys(vehiclesByLane).sort();
    const currentLaneIndex = laneIds.indexOf(currentLaneId);

    for (let i = currentLaneIndex - 1; i >= 0; i--) {
      const prevLaneId = laneIds[i];
      const prevLaneVehicles = vehiclesByLane[prevLaneId];
      if (prevLaneVehicles && prevLaneVehicles.length > 0) {
        return prevLaneVehicles[prevLaneVehicles.length - 1]; // Last car in previous lane
      }
    }

    return null; // No more cars
  }

  // Function to navigate to a car (for name/image clicks)
  const navigateToCar = (targetCar) => {
    if (!targetCar) return;

    // Navigate to the vehicle detail page
    if (targetCar.vehicle?.slug) {
      navigate(`/${targetCar.vehicle.slug}`);
    } else if (targetCar.vehicleId) {
      // If no slug, try to navigate using ID
      navigate(`/${targetCar.vehicleId}`);
    }
  };

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        // Use the action from API response to determine the new state
        const action = response.data.data?.action;
        const isAdded = action === "added";
        setIsBookmarked(isAdded);

        // Show appropriate toast message based on API response
        if (isAdded) {
          showSuccess("Vehicle added to Watch List successfully!");
        } else {
          showSuccess("Vehicle removed from Watch List successfully!");
        }
      } else {
        // Show the specific API error message
        const errorMessage =
          response.data.message ||
          "Failed to update Watch List. Please try again.";
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      // Show specific error message from API response if available
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update Watch List. Please try again.";
      showError(errorMessage);
    }
  };

  // Share functionality
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    if (car?.vehicle?.slug) {
      return `${baseUrl}/${car.vehicle.slug}`;
    }
    return window.location.href;
  };

  const getShareText = () => {
    return `Check out this amazing vehicle: ${car?.name} on Khatal Jazeera Auctions!`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      showSuccess("Link copied to clipboard!");
      setShowShareDropdown(false);
    } catch (error) {
      console.error("Failed to copy:", error);
      showError("Failed to copy link. Please try again.");
    }
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      "_blank",
      "width=600,height=400"
    );
    setShowShareDropdown(false);
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(
      `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      "_blank",
      "width=600,height=400"
    );
    setShowShareDropdown(false);
  };

  const shareOnWhatsApp = () => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}%20${url}`, "_blank");
    setShowShareDropdown(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: car?.name,
          text: getShareText(),
          url: getShareUrl(),
        });
        setShowShareDropdown(false);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error sharing:", error);
          showError("Failed to share. Please try again.");
        }
      }
    } else {
      // Fallback to copy to clipboard if native share is not supported
      copyToClipboard();
    }
  };

  // Function to show next car in header only
  const showNextCarInHeader = () => {
    if (currentNavigationIndex < vehicleList.length - 1) {
      const newIndex = currentNavigationIndex + 1;
      setCurrentNavigationIndex(newIndex);
      setDisplayedCar(vehicleList[newIndex]);

      // Update next and previous cars
      setNextCar(
        newIndex < vehicleList.length - 1 ? vehicleList[newIndex + 1] : null
      );
      setPreviousCar(newIndex > 0 ? vehicleList[newIndex - 1] : null);
    }
  };

  // Function to show previous car in header only
  const showPreviousCarInHeader = () => {
    if (currentNavigationIndex > 0) {
      const newIndex = currentNavigationIndex - 1;
      setCurrentNavigationIndex(newIndex);
      setDisplayedCar(vehicleList[newIndex]);

      // Update next and previous cars
      setNextCar(
        newIndex < vehicleList.length - 1 ? vehicleList[newIndex + 1] : null
      );
      setPreviousCar(newIndex > 0 ? vehicleList[newIndex - 1] : null);
    }
  };

  // Fetch vehicle list when component mounts or car changes
  useEffect(() => {
    const fetchVehicleList = async () => {
      if (!car?.auctionLanesVehicles?.[0]?.auctionLane?.auctionId) {
        return;
      }

      try {
        const auctionId = car.auctionLanesVehicles[0].auctionLane.auctionId;
        const currentLaneId = car.auctionLanesVehicles[0].auctionLane.laneId;

        // Fetch vehicles for the entire auction (all lanes)
        const response = await api.get(
          `/auctions/vehicles/paginated?page=1&limit=50&orderBy=id&order=DESC&search=&slug=auctions&auctionId=${auctionId}`
        );

        // The API response has the structure: { data: [...], page: 1, pages: 1, total: 4 }
        if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          // Filter vehicles to only include those from the current lane
          const laneSpecificVehicles = response.data.data.filter((vehicle) => {
            const vehicleLaneId =
              vehicle.auctionLane?.laneId || vehicle.auctionLane?.lane?.id;
            return (
              vehicleLaneId &&
              vehicleLaneId.toString() === currentLaneId.toString()
            );
          });

          // Sort vehicles by run number within the lane
          const sortedLaneVehicles = laneSpecificVehicles.sort(
            (a, b) => a.runNo - b.runNo
          );

          // Store the total vehicles in lane for display purposes
          const totalVehiclesInLane = sortedLaneVehicles.length;
          setTotalVehiclesInLane(totalVehiclesInLane);

          // Remove the current vehicle from the list (skip it)
          const currentVehicleId = car.vehicleId || car.id;
          const vehiclesWithoutCurrent = sortedLaneVehicles.filter(
            (v) => v.vehicleId !== currentVehicleId
          );

          setVehicleList(vehiclesWithoutCurrent);

          console.log("=== LANE-SPECIFIC VEHICLE FETCH ===");
          console.log("Auction ID:", auctionId);
          console.log("Current Lane ID:", currentLaneId);
          console.log("Total vehicles in lane:", sortedLaneVehicles.length);
          console.log("Current vehicle ID (skipped):", currentVehicleId);
          console.log(
            "Vehicles to show (excluding current):",
            vehiclesWithoutCurrent.length
          );
          console.log(
            "Vehicles to show:",
            vehiclesWithoutCurrent.map((v) => ({
              id: v.vehicleId,
              runNo: v.runNo,
              name: v.vehicle?.name,
            }))
          );
          console.log("=====================================");

          // Set navigation state
          if (vehiclesWithoutCurrent.length > 0) {
            setCurrentNavigationIndex(0);
            setDisplayedCar(vehiclesWithoutCurrent[0]);

            // Set next and previous cars
            setNextCar(
              vehiclesWithoutCurrent.length > 1
                ? vehiclesWithoutCurrent[1]
                : null
            );
            setPreviousCar(null); // No previous car since we start from the first available
          } else {
            // No vehicles to show (lane completed or only current vehicle in lane)
            setCurrentNavigationIndex(0);
            setDisplayedCar(null);
            setNextCar(null);
            setPreviousCar(null);
          }
        }
      } catch (error) {
        console.error("Error fetching vehicle list:", error);
      }
    };

    if (car) {
      fetchVehicleList();
    }
  }, [car]);

  // Early return if car is null
  if (!car) {
    return (
      <div className='max-w-7xl mx-auto'>
        <div className='flex flex-col md:flex-row items-center justify-between py-1 md:py-2 lg:py-2 px-1 md:'>
          <div className='flex flex-col gap-1 sm:gap-1 md:gap-1 lg:gap-1 w-full md:w-auto'>
            <h1 className='text-lg sm:text-xl md:text-2xl lg:text-[26px] font-bold'>
              Loading...
            </h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-7xl mx-auto  '>
      <div className='flex flex-col md:flex-row items-center justify-between py-1 md:py-2 lg:py-2 px-1 md: '>
        {/* -----------------------Left Side--------------------- */}
        <div className='flex flex-col gap-1 sm:gap-1 md:gap-1 lg:gap-1 w-full md:w-auto'>
          <h1 className='text-lg sm:text-xl md:text-2xl lg:text-[26px] font-bold '>
            {car?.name}
          </h1>
          <div className='flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center'>
            {car?.auctionLanesVehicles?.[0]?.runNo !== undefined &&
              car?.auctionLanesVehicles?.[0]?.runNo !== null &&
              car?.auctionLanesVehicles?.[0]?.runNo !== "" &&
              car?.auctionLanesVehicles?.[0]?.runNo !== 0 && (
                <div className='text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-bold '>
                  Run No:{" "}
                  <span className='text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-normal '>
                    {car?.auctionLanesVehicles?.[0]?.runNo}
                  </span>
                </div>
              )}
            {car?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name !==
              undefined &&
              car?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name !==
                null &&
              car?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name !==
                "" && (
                <div className='text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-bold '>
                  Lane:{" "}
                  <span className='text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-normal  text-primary'>
                    {car?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name}
                  </span>
                  {/* Location:{" "}
                  <span className="text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-normal ">
                    {car?.location}
                  </span> */}
                </div>
              )}
          </div>

          {/* Watchlist + Share */}
          <div className='flex flex-wrap sm:flex-nowrap items-center gap-1 sm:gap-1'>
            <button
              className='flex items-center'
              onClick={() => toggleWishlist(car?.id)}
            >
              <div
                className={`${
                  isBookmarked
                    ? "text-white bg-[#e55a2b]"
                    : "text-[#e55a2b] bg-white hover:text-white hover:bg-gray-300"
                } p-2 rounded-full transition-colors duration-200`}
              >
                <Bookmark size={16} className='h-6 w-6 sm:h-6 sm:w-6' />
              </div>
              <span className='text-[12px] sm:text-[14px] font-normal border-b border-black cursor-pointer'>
                {isBookmarked ? "Remove from Watchlist" : "Add To Watchlist"}
              </span>
            </button>

            <div className='relative share-dropdown-container'>
              <button
                className='flex items-center hover:text-primary rounded-full'
                onClick={() => setShowShareDropdown(!showShareDropdown)}
              >
                <div className='text-black bg-white hover:text-white p-2 rounded-full hover:bg-gray-300'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 16 17'
                    fill='black'
                  >
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M11.0003 2C9.80371 2 8.83366 2.97005 8.83366 4.16667C8.83366 4.29727 8.84521 4.42518 8.86736 4.54942L5.7136 6.75705C5.69757 6.76827 5.6824 6.78026 5.66811 6.79293C5.30028 6.50498 4.83701 6.33333 4.33366 6.33333C3.13704 6.33333 2.16699 7.30338 2.16699 8.5C2.16699 9.69662 3.13704 10.6667 4.33366 10.6667C4.83701 10.6667 5.30028 10.495 5.66811 10.2071C5.68241 10.2197 5.69757 10.2317 5.7136 10.243L8.86736 12.4506C8.84521 12.5748 8.83366 12.7027 8.83366 12.8333C8.83366 14.03 9.80371 15 11.0003 15C12.1969 15 13.167 14.03 13.167 12.8333C13.167 11.6367 12.1969 10.6667 11.0003 10.6667C10.2985 10.6667 9.67456 11.0004 9.27861 11.5178L6.29236 9.42743C6.42572 9.14628 6.50033 8.83185 6.50033 8.5C6.50033 8.16815 6.42572 7.85372 6.29236 7.57257L9.27861 5.4822C9.67456 5.99961 10.2985 6.33333 11.0003 6.33333C12.1969 6.33333 13.167 5.36328 13.167 4.16667C13.167 2.97005 12.1969 2 11.0003 2Z'
                      fill='#e55a2b'
                    />
                  </svg>
                </div>
                <span className='text-[12px] sm:text-[14px] font-normal border-b border-black cursor-pointer'>
                  Share
                </span>
              </button>

              {/* Share Dropdown */}
              {showShareDropdown && (
                <div className='absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
                  <div className='py-2'>
                    {/* Native Share (Mobile) */}
                    {navigator.share && (
                      <button
                        onClick={handleNativeShare}
                        className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                      >
                        <Share2 size={16} />
                        Share
                      </button>
                    )}

                    {/* Copy Link */}
                    <button
                      onClick={copyToClipboard}
                      className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                    >
                      <Copy size={16} />
                      Copy Link
                    </button>

                    {/* Facebook */}
                    <button
                      onClick={shareOnFacebook}
                      className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                    >
                      <Facebook size={16} className='text-blue-600' />
                      Facebook
                    </button>

                    {/* Twitter */}
                    <button
                      onClick={shareOnTwitter}
                      className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                    >
                      <Twitter size={16} className='text-blue-400' />
                      Twitter
                    </button>

                    {/* WhatsApp */}
                    <button
                      onClick={shareOnWhatsApp}
                      className='w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2'
                    >
                      <MessageCircle size={16} className='text-green-600' />
                      WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* -----------------------Right Side--------------------- */}
        <div className='w-full md:w-[652px] flex  sm:flex-row justify-center items-center gap-3 sm:gap-5 mt-4 md:mt-0  sm:px-5 py-2 sm:py-4 bg-darkGraybg rounded-md border shadow-md'>
          {(() => {
            return vehicleList && vehicleList.length > 0;
          })() ? (
            <div className='flex items-center gap-2'>
              {/* Previous Car Button */}
              <div
                className={`w-[50px] sm:w-[64px] h-[50px] sm:h-[64px] border-[1px] flex justify-center items-center rounded-md border-[#D6D6D6] bg-white ${
                  previousCar
                    ? "cursor-pointer hover:bg-gray-50"
                    : "cursor-not-allowed opacity-50"
                }`}
                onClick={() => previousCar && showPreviousCarInHeader()}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='15'
                  height='25'
                  viewBox='0 0 15 25'
                  fill='none'
                >
                  <path
                    d='M0.58 13.75L11.36 24.5C11.7 24.83 12.1 25 12.57 25C13.04 25 13.46 24.83 13.82 24.5C14.18 24.16 14.36 23.75 14.36 23.25C14.36 22.75 14.17 22.33 13.78 22L4.26 12.5L13.78 3C14.17 2.66 14.36 2.25 14.36 1.75C14.36 1.25 14.18 0.83 13.82 0.5C13.46 0.16 13.04 0 12.57 0C12.1 0 11.7 0.16 11.36 0.5L0.58 11.25C0.19 11.58 0 12 0 12.5C0 13 0.19 13.41 0.58 13.75Z'
                    fill='#414042'
                  />
                </svg>
              </div>

              {/* Car Information Display */}
              <div className='flex-1 flex items-center gap-2 sm:gap-4'>
                <img
                  src={displayedCar?.vehicle?.images?.[0]?.imageUrl}
                  alt='car'
                  className='h-[60px] sm:h-[79px] w-[80px] sm:w-[106px] border-gray-100 border-[1px] rounded-md cursor-pointer hover:opacity-80 transition-opacity'
                  onClick={() =>
                    displayedCar &&
                    window.open(`/${displayedCar.vehicle.slug}`, "_blank")
                  }
                />
                <div>
                  <h1
                    className='text-[20px] sm:text-[20px] text-white font-bold cursor-pointer hover:text-orange-300 transition-colors'
                    onClick={() =>
                      displayedCar &&
                      window.open(`/${displayedCar.vehicle.slug}`, "_blank")
                    }
                  >
                    {displayedCar?.vehicle?.name || "-"}
                  </h1>
                  <div className='flex flex-col sm:flex-row gap-1 sm:gap-3'>
                    <h1 className='text-[14px] sm:text-[16px] font-bold text-white'>
                      Run No:{" "}
                      <span className='font-normal'>
                        <span className='inline-block bg-[#E95923] text-white text-xs font-bold px-2 py-1 rounded'>
                          {(() => {
                            if (!displayedCar) return "-";

                            // Show the actual run number of the displayed car and total vehicles in lane
                            return `${
                              displayedCar.runNo || "-"
                            } of ${totalVehiclesInLane}`;
                          })()}
                        </span>
                      </span>
                    </h1>
                    <h1 className='text-[14px] sm:text-[16px] text-white font-bold'>
                      Lane:{" "}
                      <span className='text-white'>
                        {displayedCar?.auctionLane?.lane?.name ||
                          `Lane ${displayedCar?.auctionLane?.laneId || "-"}`}
                      </span>
                    </h1>
                    <h1 className='text-[14px] sm:text-[16px] text-white font-bold'>
                      Current Bid:{" "}
                      <span className='text-white'>
                        {displayedCar?.vehicle?.latestBid ||
                          displayedCar?.vehicle?.startingBid ||
                          "-"}
                      </span>
                    </h1>
                  </div>
                </div>
              </div>

              {/* Next Car Button */}
              <div
                className={`w-[50px] sm:w-[64px] h-[50px] sm:h-[64px] border-[1px] flex justify-center items-center rounded-md border-[#D6D6D6] bg-white ${
                  nextCar
                    ? "cursor-pointer hover:bg-gray-50"
                    : "cursor-not-allowed opacity-50"
                }`}
                onClick={() => nextCar && showNextCarInHeader()}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='15'
                  height='25'
                  viewBox='0 0 15 25'
                  fill='none'
                >
                  <path
                    d='M13.78 13.75L3 24.5C2.67 24.83 2.27 25 1.79 25C1.32 25 0.9 24.83 0.54 24.5C0.18 24.16 0 23.75 0 23.25C0 22.75 0.19 22.33 0.58 22L10.1 12.5L0.58 3C0.19 2.66 0 2.25 0 1.75C0 1.25 0.18 0.83 0.54 0.5C0.9 0.16 1.32 0 1.79 0C2.27 0 2.67 0.16 3 0.5L13.78 11.25C14.17 11.58 14.36 12 14.36 12.5C14.36 13 14.17 13.41 13.78 13.75Z'
                    fill='#242424'
                  />
                </svg>
              </div>
            </div>
          ) : (
            <div>
              <h1 className='text-[14px] sm:text-[16px] font-bold text-white'>
                No more cars available
              </h1>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarDetailHeader;
